/**
 * Classe contenant des outils utiles à travers l'application.
 */
export class Tools {
  /**
   * Retourne une promesse qui se résout après un délai donné.
   * @param ms Le nombre de millisecondes à attendre.
   * @returns Une promesse qui se résout après `ms` millisecondes.
   */
  public static timeout(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Attend un délai donné (3000ms), puis exécute une fonction avec des arguments optionnels.
   * @param fn La fonction à exécuter.
   * @param args Les arguments à passer à la fonction.
   * @returns Le résultat de la fonction.
   */
  public static async sleep(fn: Function, ...args: any) {
    await Tools.timeout(3000);
    return fn(...args);
  }

  /**
   * Génère une chaîne aléatoire de la longueur donnée.
   * @param length La longueur de la chaîne à générer.
   * @returns Une chaîne aléatoire.
   */
  public static randomString(length: number) {
    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    var result = '';
    for (var i = length; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
    return result;
  }

  /**
   * Obtient la valeur d'un enum à partir de la clé sous forme de chaîne.
   * @param key La clé de l'enum sous forme de chaîne.
   * @param enumObject L'objet enum.
   * @returns La valeur correspondante, ou `undefined` si la clé n'existe pas.
   */
  public static getEnumValueFromKeyString<T extends Object>(key: keyof any, enumObject: T): T[keyof T] | undefined {
    if (key in enumObject) {
      return enumObject[key as keyof T];
    } else {
      throw new Error(`Key ${String(key)} does not exist in enum`);
    }
  }

  /**
   * Obtient la clé d'un enum à partir de la valeur.
   * @param value La valeur de l'enum.
   * @param enumObject L'objet enum.
   * @returns La clé correspondante, ou `undefined` si la valeur n'existe pas.
   */
  public static getKeyStringFromEnumValue<T extends Record<string, any>>(value: T[keyof T], enumObject: T): keyof T | undefined {
    for (const key in enumObject) {
      if (enumObject[key] === value) {
        return key as keyof T;
      }
    }
    return undefined;
  }

  /**
   * Convertit la première lettre d'une chaîne en majuscule et le reste en minuscule.
   * @param input La chaîne à capitaliser.
   * @returns La chaîne capitalisée.
   */
  public static capitalizeString(input: string): string {
    const lowercase = input.toLowerCase();
    return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
  }
}
